import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { ServiciosDelTipo } from "@/componentes/catalogos/ServiciosDelTipo"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

type Categoria = "OPERATIVO" | "ADMINISTRATIVO" | "SERVICIO_PROPIO"

type Tipo = {
  id: number
  nombre: string
  categorias: Categoria[]
  activo: boolean
  /** Cuántos proveedores activos cuelgan de él: lo que hay que saber antes de tocarlo. */
  proveedores: number
}

/** Qué significa cada categoría, en la pantalla y no sólo en el código. */
const CATEGORIAS: { valor: Categoria; etiqueta: string; ayuda: string }[] = [
  {
    valor: "OPERATIVO",
    etiqueta: "Operativo",
    ayuda: "Se le cargan gastos del expediente: almacenadora, transporte, agente naviero.",
  },
  {
    valor: "ADMINISTRATIVO",
    etiqueta: "Administrativo",
    ayuda: "Gasto de la agencia, no del despacho. Es a quien se le redacta una orden de pago.",
  },
  {
    valor: "SERVICIO_PROPIO",
    etiqueta: "Servicio propio",
    ayuda: "Un servicio que presta la agencia misma. No es un tercero al que se le pague.",
  },
]

const ETIQUETA: Record<Categoria, string> = {
  OPERATIVO: "Operativo",
  ADMINISTRATIVO: "Administrativo",
  SERVICIO_PROPIO: "Servicio propio",
}

const TONO: Record<Categoria, "info" | "primario" | "neutro"> = {
  OPERATIVO: "info",
  ADMINISTRATIVO: "primario",
  SERVICIO_PROPIO: "neutro",
}

/**
 * Los tipos de proveedor: «Almacenadora», «Alquileres», «Servicios básicos».
 *
 * ⚠ **La categoría no es una etiqueta: decide DÓNDE se puede usar el proveedor.** Un
 * operativo recibe gastos del expediente; a un administrativo se le redacta una orden
 * de pago; un servicio propio no es un tercero. El proveedor no la guarda — la hereda
 * de su tipo—, así que corregir un tipo mal clasificado mueve de selector a todos los
 * suyos. Por eso la pantalla dice cuántos son ANTES de guardar.
 *
 * ⚠ **Se archivan, no se borran**, como las categorías de gasto y los bancos: los
 * proveedores de años anteriores lo siguen nombrando. Y no se archiva uno con
 * proveedores activos dentro: quedarían apuntando a un tipo que ya no se puede
 * elegir, y quien abra esa ficha no sabrá si eso es lo normal o un error.
 *
 * ⚠ **Cada tipo lleva sus SERVICIOS** (04/11, `ServiciosDelTipo`): «Almacenaje» se crea una
 * vez en «Almacenadora» y cada almacenadora marca en su ficha si lo presta y a qué precio.
 */
export function TiposProveedor() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [enEdicion, setEnEdicion] = useState<Tipo | null>(null)
  const vista = useRef<HTMLElement | null>(null)
  const [categorias, setCategorias] = useState<Categoria[]>(["OPERATIVO"])

  const puedeAdministrar = tienePermiso(usuario, "proveedor:administrar")

  const tipos = useQuery({
    queryKey: ["tipos-proveedor"],
    queryFn: () => api.get<Tipo[]>("/api/v1/tipos-proveedor"),
  })

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      enEdicion
        ? api.patch(`/api/v1/tipos-proveedor/${enEdicion.id}`, cuerpo)
        : api.post("/api/v1/tipos-proveedor", cuerpo),
    onSuccess: () => {
      // También la lista de proveedores: su tipo —y con él su categoría— cambió.
      clienteQuery.invalidateQueries({ queryKey: ["tipos-proveedor"] })
      clienteQuery.invalidateQueries({ queryKey: ["proveedores"] })
      setAbierto(false)
    },
  })

  function abrir(tipo: Tipo | null) {
    guardar.reset()
    setEnEdicion(tipo)
    setCategorias(tipo?.categorias ?? ["OPERATIVO"])
    setAbierto(true)
  }

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const cuerpo: Record<string, unknown> = {
      nombre: String(datos.get("nombre") ?? "").trim(),
      categorias,
    }
    if (enEdicion) cuerpo.activo = datos.get("activo") === "on"
    guardar.mutate(cuerpo)
  }

  if (tipos.isPending) return <p className="py-6">Cargando…</p>
  if (tipos.isError) {
    return (
      <p
        className="flex items-center gap-2 py-6"
        role="alert"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} /> No se pudieron cargar los tipos de proveedor.
      </p>
    )
  }

  const filas = tipos.data
  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null
  // Cambiar las categorías de un tipo con proveedores los mueve a todos de selector.
  const cambiaron =
    enEdicion !== null && [...categorias].sort().join() !== [...enEdicion.categorias].sort().join()
  const arrastra = cambiaron && (enEdicion?.proveedores ?? 0) > 0

  return (
    <section ref={vista} className="space-y-4">
      {/* Con título propio desde que «Comercial» es una sección del menú y no una
          pantalla con pestañas: esta vista ya no cuelga de ninguna otra. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Tipos de proveedor</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Cómo se clasifica un proveedor, y con eso dónde se le puede usar: en el expediente o en
            una orden de pago.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar titulo="Tipos de proveedor" contenido={vista} />
          {puedeAdministrar && (
            <Boton variante="primario" onClick={() => abrir(null)}>
              <Plus size={16} /> Nuevo tipo
            </Boton>
          )}
        </div>
      </div>

      <Tabla etiqueta="Tipos de proveedor">
        <Cabecera>
          <Th>Tipo</Th>
          <Th>Categoría</Th>
          <Th derecha>Proveedores</Th>
          <Th>Estado</Th>
        </Cabecera>
        <Cuerpo>
          {filas.length === 0 && (
            <TablaVacia colSpan={4}>
              Todavía no hay tipos. El primero suele ser «Almacenadora».
            </TablaVacia>
          )}
          {filas.map((tipo) => (
            <Fila
              key={tipo.id}
              atenuada={!tipo.activo}
              className={puedeAdministrar ? "cursor-pointer" : undefined}
              onClick={puedeAdministrar ? () => abrir(tipo) : undefined}
            >
              <Td>{tipo.nombre}</Td>
              <Td data-exportar-valor={tipo.categorias.map((c) => ETIQUETA[c]).join(", ")}>
                <span className="flex flex-wrap gap-1">
                  {tipo.categorias.map((c) => (
                    <Insignia key={c} tono={TONO[c]}>
                      {ETIQUETA[c]}
                    </Insignia>
                  ))}
                </span>
              </Td>
              <Td derecha numero>
                {tipo.proveedores}
              </Td>
              <Td>
                {tipo.activo ? (
                  <Insignia tono="exito">Activo</Insignia>
                ) : (
                  <Insignia tono="neutro">Archivado</Insignia>
                )}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      <Modal
        abierto={abierto}
        titulo={enEdicion ? `Editar ${enEdicion.nombre}` : "Nuevo tipo de proveedor"}
        ancho={enEdicion ? "lg" : "sm"}
        onCerrar={() => setAbierto(false)}
      >
        <form key={enEdicion?.id ?? "nuevo"} className="space-y-4" onSubmit={enviar}>
          <Campo para="tipo-nombre" etiqueta="Nombre">
            <input
              id="tipo-nombre"
              name="nombre"
              required
              minLength={2}
              maxLength={80}
              defaultValue={enEdicion?.nombre ?? ""}
              className="campo mt-1"
            />
          </Campo>

          {/* ⚠ **Varias, y al menos una.** «Entes gubernamentales» es operativo —la
              tasa del puerto va al expediente— y administrativo —la patente municipal
              es gasto de la agencia—. Qué significa cada una va al lado y no en un
              manual: es lo que decide dónde se puede usar el proveedor. */}
          <fieldset>
            <legend className="text-sm font-medium">Categorías</legend>
            <div className="mt-1 space-y-2">
              {CATEGORIAS.map((c) => (
                <label key={c.valor} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={categorias.includes(c.valor)}
                    onChange={(e) =>
                      setCategorias((actuales) =>
                        e.target.checked
                          ? [...actuales, c.valor]
                          : actuales.filter((v) => v !== c.valor),
                      )
                    }
                  />
                  <span>
                    {c.etiqueta}
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {c.ayuda}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {categorias.length === 0 && (
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Un tipo sin categoría no sirve para nada: marca al menos una.
            </p>
          )}

          {arrastra && (
            <p className="text-xs" role="status" style={{ color: "var(--warning)" }}>
              {enEdicion?.proveedores === 1
                ? "1 proveedor cambia con este tipo"
                : `${enEdicion?.proveedores} proveedores cambian con este tipo`}
              : cambia dónde se les puede usar.
            </p>
          )}

          {enEdicion && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="activo" defaultChecked={enEdicion.activo} />
              Activo
            </label>
          )}

          {error && (
            <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Boton onClick={() => setAbierto(false)}>Cancelar</Boton>
            <Boton
              type="submit"
              variante="primario"
              disabled={guardar.isPending || categorias.length === 0}
            >
              {guardar.isPending ? "Guardando…" : "Guardar"}
            </Boton>
          </div>
        </form>

        {/* Fuera del <form> del tipo: el de cada servicio es otro formulario, y un
            formulario no puede ir dentro de otro. */}
        {enEdicion && (
          <div className="mt-6 border-t pt-4" style={{ borderColor: "var(--border)" }}>
            <ServiciosDelTipo tipoId={enEdicion.id} tipoNombre={enEdicion.nombre} />
          </div>
        )}
      </Modal>
    </section>
  )
}
